import React, { useEffect, useState, useRef, useCallback } from "react";
import FastAPIClient from "../../client";
import config from "../../config";
import { useNetwork } from "../../components/NetworkProvider";
import ClientFilters from "../../components/Filters/ClientFilters";
import ClientHeader from "./ClientHeader";
import ClientNew from "../../components/LayoutComponents/ClientNew";
import ClientCardSkeleton from "../../components/Skeletons/ClientCardSkeleton";
import "./style.css";
import { IntlProvider } from "react-intl";
import {
    blankExpandedLevels,
    blankProspectLevels,
    blankProspectFilters,
    blankFilters,
    blankStatusFilters,
    initial_subCategories,
    initial_prospect_subCategories,
} from "../../utils/Clients/mapping";
import {
    handleCreateClient,
    filterOffline,
} from "../../utils/Clients/functions";
import messages_en from "./translations/en.json";
import messages_fr from "./translations/fr.json";
import CreateClientModal from "../../components/ModalComponents/CreateClientModal";
import FiltersMobileModal from "../../components/ModalComponents/FiltersMobileModal";
import { useSearchParams } from "react-router-dom";

const client = new FastAPIClient(config);

const messages = {
    en: messages_en,
    fr: messages_fr,
};

const ClientList = ({ clients, language }) => {
    return (
        <>
            {clients.length &&
                clients.map((client, index) => (
                    <ClientNew
                        isFirst={index === 0}
                        isLast={index === clients.length - 1}
                        alert={client.alert}
                        type={client.type}
                        key={client.client_id}
                        client={client}
                        language={language}
                        last_interaction={client.last_meeting_start_date}
                        unique_result={clients.length === 1}
                    />
                ))}
            {!clients.length && (
                <p>
                    Pas de clients trouvés avec les critères de recherche
                    définis
                </p>
            )}
        </>
    );
};

const ClientDashboard = () => {
    const limit = 12;
    const [clients, setClients] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [skip, setSkip] = useState(0);
    const [locale, setLocale] = useState();
    const [refreshing, setRefreshing] = useState(true);
    const [batchRefreshing, setBatchRefreshing] = useState(false);
    const [clientCount, setClientCount] = useState(null);
    const [leadCount, setLeadCount] = useState(null);
    const [sortOption, setSortOption] = useState(
        "last_meeting_start_date_desc"
    ); // Default sort option
    const [user, setUser] = useState([]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    // State to hold selected filters

    const { isOnline } = useNetwork();

    const [searchParams, setSearchParams] = useSearchParams();
    const showLeads = searchParams.get("showLeads") === "true";
    const [subCategories, setSubCategories] = useState(
        showLeads ? initial_prospect_subCategories : initial_subCategories
    );
    const [selectedFilters, setSelectedFilters] = useState(
        showLeads ? blankProspectFilters : blankFilters
    );
    const [expandedLevels, setExpandedLevels] = useState(
        showLeads ? blankProspectLevels : blankExpandedLevels
    );
    const [statusFilters, setStatusFilters] = useState(blankStatusFilters);
    const [daysFilters, setDaysFilters] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [mobileFilters, setMobileFilters] = useState(blankFilters);
    const [mobileStatusFilters, setMobileStatusFilters] =
        useState(blankStatusFilters);
    const [mobileArrayFilters, setMobileArrayFilters] = useState([]);
    const toggleShowLeads = useCallback(() => {
        setSearchParams({ showLeads: (!showLeads).toString() });
    }, [showLeads, setSearchParams]);

    const containerRef = useRef(null);
    const endOfContainerRef = useRef(null);
    const fetchingRef = useRef(false);
    const goToNext = useCallback(() => {
        if (user.user_id) {
            const nextSkip = skip + limit;
            setSkip(nextSkip);
            fetchUserClientsOnline(
                nextSkip,
                showLeads,
                selectedFilters,
                statusFilters,
                daysFilters,
                sortOption
            );
        }
    }, [
        skip,
        showLeads,
        selectedFilters,
        statusFilters,
        daysFilters,
        sortOption,
        user.user_id,
    ]);

    useEffect(() => {
        if (user.user_id) {
            setClients([]);
            setSkip(0);
            setRefreshing(true);
            setBatchRefreshing(false);
            fetchingRef.current = false;
            setSubCategories(
                showLeads
                    ? initial_prospect_subCategories
                    : initial_subCategories
            );
            setSelectedFilters(showLeads ? blankProspectFilters : blankFilters);
            setStatusFilters(blankStatusFilters);
            setDaysFilters([]);
            setMobileArrayFilters([]);
            setMobileStatusFilters(blankStatusFilters);
            setMobileFilters(showLeads ? blankProspectFilters : blankFilters);
            setExpandedLevels(
                showLeads ? blankProspectLevels : blankExpandedLevels
            );
        }
    }, [showLeads]);

    useEffect(() => {
        setLocale(localStorage.getItem("language"));
        getUser();
    }, []);

    // Initial fetch when component mounts and user data is available
    useEffect(() => {
        if (user.user_id) {
            if (isOnline) {
                setClients([]);
                setSkip(0);
                setRefreshing(true);
                fetchingRef.current = false;
            } else {
                fetchUserClientsOffline();
            }
        }
    }, [user.user_id, isOnline]);

    // Handle filter changes
    useEffect(() => {
        if (isOnline && user.user_id) {
            setRefreshing(true);
            setSkip(0);
            fetchingRef.current = false;
            if (showLeads) {
                setLeadCount(null);
            } else {
                setClientCount(null);
            }
            fetchClientCount(
                showLeads,
                selectedFilters,
                statusFilters,
                daysFilters
            );
            fetchUserClientsOnline(
                0,
                showLeads,
                selectedFilters,
                statusFilters,
                daysFilters,
                sortOption
            );
        } else if (!isOnline && user.user_id) {
            filterOffline(
                allClients,
                showLeads,
                selectedFilters,
                statusFilters,
                daysFilters,
                sortOption,
                setClients,
                setLeadCount,
                setClientCount
            );
        }
    }, [
        user.user_id,
        isOnline,
        selectedFilters,
        statusFilters,
        daysFilters,
        sortOption,
        allClients,
    ]);
    useEffect(() => {
        if (isOnline) {
            const handleScrollEnd = () => {
                if (
                    clients.length > 0 &&
                    !refreshing &&
                    !fetchingRef.current &&
                    !batchRefreshing &&
                    (showLeads
                        ? clients.length < leadCount
                        : clients.length < clientCount)
                ) {
                    fetchingRef.current = true;
                    setBatchRefreshing(true);
                    goToNext();
                }
            };
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        handleScrollEnd();
                    }
                },
                {
                    root: null,
                    threshold: 0.9,
                }
            );

            if (endOfContainerRef.current) {
                observer.observe(endOfContainerRef.current);
            }

            return () => {
                if (endOfContainerRef.current) {
                    observer.unobserve(endOfContainerRef.current);
                }
            };
        }
    }, [
        clients.length,
        isOnline,
        refreshing,
        batchRefreshing,
        showLeads,
        leadCount,
        clientCount,
        selectedFilters,
        statusFilters,
        daysFilters,
        sortOption,
    ]);

    const reset = useCallback(() => {
        setSelectedFilters(blankFilters);
        setStatusFilters(blankStatusFilters);
        setDaysFilters([]);
        setMobileStatusFilters(blankStatusFilters);
        setMobileArrayFilters([]);
        setMobileStatusFilters(blankStatusFilters);
        setMobileFilters(blankFilters);
        setExpandedLevels(blankExpandedLevels);
    }, []);

    const getUser = () => {
        client.fetchUser().then((data) => {
            setUser(data);
        });
    };

    const fetchUserClientsOffline = () => {
        if (user.user_id != null) {
            client.getUserClients(user.user_id, null).then((data) => {
                const sortedResults = data.sort((a, b) => {
                    // Move objects with 'alert' as true to the beginning
                    return b.alert - a.alert; // b.alert - a.alert will move true values to the top
                });
                setAllClients(sortedResults);
            });
        } else {
            console.log("user_id is null or undefined, skipping API call.");
        }
    };

    const fetchUserClientsOnline = useCallback(
        (skip, is_lead, filter, statusFilters, visit_days, sortOption) => {
            client
                .getUserClientswInfo({
                    skip,
                    limit: 12,
                    user_id: user.user_id,
                    is_lead,
                    filter,
                    status_filter: statusFilters,
                    visit_days,
                    sort_by: sortOption,
                })
                .then((data) => {
                    if (skip === 0) {
                        setClients(data);
                    } else {
                        setClients((prevClients) => [...prevClients, ...data]);
                    }
                    setRefreshing(false);
                    setBatchRefreshing(false);
                    fetchingRef.current = false;
                })
                .catch((error) => {
                    console.error("Error fetching clients:", error);
                    if (error.status === 404) {
                        setClients([]);
                    }
                    setRefreshing(false);
                    setBatchRefreshing(false);
                    fetchingRef.current = false;
                });
        },
        [user.user_id]
    );

    const fetchClientCount = useCallback(
        (is_lead, filter, statusFilters, visit_days) => {
            client
                .getClientCount(
                    { user_id: user.user_id, is_lead },
                    { filter, status_filter: statusFilters, visit_days }
                )
                .then((count) => {
                    if (is_lead) {
                        setLeadCount(count);
                    } else {
                        setClientCount(count);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching client count:", error);
                    if (is_lead) {
                        setLeadCount(0);
                    } else {
                        setClientCount(0);
                    }
                });
        },
        [user.user_id]
    );

    useEffect(() => {
        if (clients.length > 0) {
            setRefreshing(false);
        }
    }, [clients]);

    return (
        <IntlProvider
            locale={locale}
            messages={messages[locale]}
            onError={(err) => {
                if (err.code === "MISSING_TRANSLATION") {
                    // Ignore missing translation warnings
                    return;
                }
            }}
        >
            {isFilterOpen && (
                <FiltersMobileModal setIsFilterOpen={setIsFilterOpen}>
                    <ClientFilters
                        showLeads={showLeads}
                        subCategories={subCategories}
                        setSubCategories={setSubCategories}
                        expandedLevels={expandedLevels}
                        setExpandedLevels={setExpandedLevels}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                        reset={reset}
                        user_id={user.user_id}
                        statusFilters={statusFilters}
                        setStatusFilters={setStatusFilters}
                        mobileFilterOpen={true}
                        mobileFilters={mobileFilters}
                        setMobileFilters={setMobileFilters}
                        isFilterOpen={isFilterOpen}
                        setIsFilterOpen={setIsFilterOpen}
                        mobileStatusFilters={mobileStatusFilters}
                        setMobileStatusFilters={setMobileStatusFilters}
                        mobileArrayFilters={mobileArrayFilters}
                        setMobileArrayFilters={setMobileArrayFilters}
                    />
                </FiltersMobileModal>
            )}
            <div className="flex flex-col md:flex-row h-screen overflow-y-auto w-full overflow-x-hidden relative bg-white">
                <div className="w-full bg-white">
                    <ClientHeader
                        showLeads={showLeads}
                        toggleShowLeads={toggleShowLeads}
                        setIsCreateModalOpen={setIsCreateModalOpen}
                        setIsFilterOpen={setIsFilterOpen}
                        isFilterOpen={isFilterOpen}
                        sortOption={sortOption}
                        setSortOption={setSortOption}
                        user_id={user.user_id}
                        allClients={allClients}
                    />

                    <div className="flex flex-col md:flex-row w-full mb-2 md:pr-4 justify-start">
                        <div className="flex flex-col w-full md:px-0"></div>
                    </div>

                    <div
                        ref={containerRef}
                        className="text-white flex w-full items-start px-1 md:px-4 text-left rounded-tr-md rounded-b-md pb-5"
                    >
                        <div
                            className={`w-full md:w-4/5 transition-all duration-200 ease-in-out text-left rounded-lg overflow-y-auto gray-50 justify-start px-3 md:px-0
                                    ${
                                        isFilterOpen
                                            ? "md:w-4/5"
                                            : "w-full md:w-full"
                                    }`}
                        >
                            {!refreshing ? (
                                <>
                                    {clients.length > 0 ? (
                                        <ClientList
                                            clients={clients}
                                            language={locale}
                                        />
                                    ) : (
                                        <p className="text-black">
                                            Pas de résultat correspondant aux
                                            critères de recherche.
                                        </p>
                                    )}
                                </>
                            ) : (
                                Array.from({ length: limit }).map((_, idx) => (
                                    <ClientCardSkeleton key={idx} />
                                ))
                            )}
                            {batchRefreshing &&
                                Array.from({ length: limit }).map((_, idx) => (
                                    <ClientCardSkeleton key={idx} />
                                ))}

                            <div ref={endOfContainerRef} className="h-32" />
                        </div>

                        <div
                            className={`text-black ${
                                isFilterOpen
                                    ? "hidden w-full md:block md:w-1/5"
                                    : "max-w-0 hidden"
                            }  delay-150 duration-300 ease-in-out md:max-h-screen h-full transition-all pl-3  relative transition-all `}
                        >
                            <ClientFilters
                                {...{
                                    showLeads,
                                    subCategories,
                                    setSubCategories,
                                    expandedLevels,
                                    setExpandedLevels,
                                    selectedFilters,
                                    setSelectedFilters,
                                    reset,
                                    statusFilters,
                                    setStatusFilters,
                                    daysFilters,
                                    setDaysFilters,
                                    isFilterOpen,
                                    setIsFilterOpen,
                                    mobileStatusFilters,
                                    setMobileStatusFilters,
                                    mobileArrayFilters,
                                    setMobileArrayFilters,
                                }}
                                mobileFilterOpen={false}
                                user_id={user.user_id}
                            />
                        </div>
                    </div>
                </div>

                <CreateClientModal
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    onCreate={handleCreateClient}
                />
            </div>
        </IntlProvider>
    );
};

export default ClientDashboard;
